/**
 * 근태 문서 상세 모달 — 내 신청 문서 · 내 결재 문서 · 근태 신청 현황 공용
 * (2026-09-18 결재 UX 라운드, spec docs/superpowers/specs/2026-09-18-attendance-approval-ux-design.md).
 *
 * - 행(`.att-row[data-detail]`, 표의 <tr> 이거나 결재함의 <button>)이나 「상세」 버튼을
 *   누르면 서버가 #apprDetails 에 숨겨 둔 상세 블록을 모달로 옮긴다(원래 방식).
 * - 모달 머리의 ‹ k / N › 는 같은 그룹(data-group)의 보이는 행 안에서 오간다.
 * - 승인/반려: 폼에 data-inline="1" 이면(내 결재 문서) fetch(X-Work-Inline) 로 보내고,
 *   같은 URL 을 다시 받아 #attApprovals 를 제자리 교체한 뒤 다음 대기 문서를 연다.
 *   그 밖(관리자 대행)은 예전처럼 폼 POST + 리다이렉트. JS 가 없어도 폼은 그대로 동작한다.
 * - 상태 칩(`[data-att-filter]`)은 클라이언트 필터이고 sessionStorage 에 남는다.
 * - 플래시(#attendance-req-flash)는 토스트로 — 블록 alert 는 레이아웃을 민다.
 */
const FILTER_KEY = 'att.filter.';

function toast(message, type) {
    if (message && window.showNotification) window.showNotification(message, type || 'info');
}

function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : '';
}

// ── 상태 칩 필터 (모달과 무관하게 세 화면 모두) ──
function rowsFor(key) {
    if (key === 'requests') return Array.from(document.querySelectorAll('#attRequestsTable tr.att-row'));
    return Array.from(document.querySelectorAll('[data-att-rows="' + key + '"] .att-row'));
}

function readFilter(key) {
    try { return sessionStorage.getItem(FILTER_KEY + key) || 'all'; } catch (e) { return 'all'; }
}

export function applyAttendanceFilters() {
    document.querySelectorAll('[data-att-filter-group]').forEach((group) => {
        const key = group.dataset.attFilterGroup;
        const val = readFilter(key);
        group.querySelectorAll('[data-att-filter]').forEach((chip) => {
            const on = chip.dataset.attFilter === val;
            chip.classList.toggle('active', on);
            chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const rows = rowsFor(key);
        let shown = 0;
        rows.forEach((row) => {
            const on = val === 'all' || row.dataset.status === val;
            row.classList.toggle('att-hidden', !on);
            if (on) shown += 1;
        });
        const empty = document.querySelector('[data-att-no-match="' + key + '"]');
        if (empty) empty.hidden = shown > 0 || rows.length === 0;
    });
}

function setupFilters() {
    if (document.body.dataset.attFiltersBound) return;
    document.body.dataset.attFiltersBound = '1';
    document.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-att-filter]');
        if (!chip) return;
        const group = chip.closest('[data-att-filter-group]');
        if (!group) return;
        try { sessionStorage.setItem(FILTER_KEY + group.dataset.attFilterGroup, chip.dataset.attFilter); } catch (err) { /* 무해 */ }
        applyAttendanceFilters();
    });
    // 연도 select (AO7) — 서버 필터라 주소만 바꾼다 (근태 화면은 SPA 가 아니다).
    document.addEventListener('change', (e) => {
        if (!(e.target instanceof HTMLSelectElement) || !e.target.matches('[data-att-year]')) return;
        const url = new URL(window.location.href);
        if (e.target.value) url.searchParams.set('year', e.target.value); else url.searchParams.delete('year');
        ['success', 'error'].forEach((k) => url.searchParams.delete(k));
        window.location.assign(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
    });
    applyAttendanceFilters();
}

function fmtCount(tpl, n, m) {
    return String(tpl || '').replace('%d', n).replace('%d', m == null ? '' : m);
}

// ── 플래시 → 토스트 + 주소 정리 ──
function showFlash() {
    const flash = document.getElementById('attendance-req-flash');
    if (flash && flash.dataset.message && !flash.dataset.shown) {
        flash.dataset.shown = '1';
        toast(flash.dataset.message, flash.dataset.error === '1' ? 'error' : 'success');
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('success') || url.searchParams.has('error')) {
        ['success', 'error'].forEach((k) => url.searchParams.delete(k));
        window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash);
    }
}

export function setupAttendanceApproval() {
    setupFilters();
    const modal = document.getElementById('attApprovalModal');
    const body = document.getElementById('attApprovalBody');
    if (!modal || !body || modal.dataset.ready) {
        if (document.getElementById('attendance-req-flash') && !document.getElementById('attReqModal')) showFlash();
        return;
    }
    modal.dataset.ready = '1';
    showFlash();

    const prevBtn = document.getElementById('attApprovalPrev');
    const nextBtn = document.getElementById('attApprovalNext');
    const posEl = document.getElementById('attApprovalPos');
    const closeBtn = document.getElementById('attApprovalClose');
    let openDetail = null;
    let openRow = null;
    let lastFocus = null;

    const stash = () => document.getElementById('apprDetails');
    const visibleRows = (group) => Array.from(document.querySelectorAll('.att-row[data-detail][data-group="' + group + '"]'))
        .filter((row) => !row.classList.contains('att-hidden'));
    const siblings = () => (openRow ? visibleRows(openRow.dataset.group) : []);
    // 결정할 수 있는 행: 같은 그룹의 대기 행 중 상세에 결재 폼이 있는 것 (내 차례 또는 대행).
    const decidableRows = (group) => visibleRows(group).filter((row) => {
        const detail = document.getElementById(row.dataset.detail);
        return row.dataset.status === 'pending' && detail && detail.querySelector('form.att-appr-decide');
    });

    function returnDetail() {
        if (!openDetail) return;
        const s = stash();
        if (s) s.appendChild(openDetail); // 상세 블록을 원위치로 반납
        openDetail = null;
    }
    function close() {
        returnDetail();
        modal.style.display = 'none';
        document.body.classList.remove('modal-scroll-lock');
        const back = lastFocus;
        openRow = null;
        lastFocus = null;
        if (back && document.contains(back)) back.focus({ preventScroll: true });
    }
    function paintNav() {
        const list = siblings();
        const i = list.indexOf(openRow);
        const many = list.length > 1;
        prevBtn.hidden = !many;
        nextBtn.hidden = !many;
        posEl.hidden = !many;
        if (many) {
            prevBtn.disabled = i <= 0;
            nextBtn.disabled = i >= list.length - 1;
            posEl.textContent = (i + 1) + ' / ' + list.length;
        }
    }
    function open(row, opts) {
        opts = opts || {};
        const detail = document.getElementById(row.dataset.detail);
        if (!detail) return false;
        returnDetail();
        openDetail = detail;
        openRow = row;
        if (!opts.keepFocus) lastFocus = document.activeElement;
        body.appendChild(detail);
        modal.style.display = 'flex';
        document.body.classList.add('modal-scroll-lock');
        body.scrollTop = 0;
        paintNav();
        const note = detail.querySelector('textarea[name="note"]');
        (note || closeBtn).focus({ preventScroll: true });
        return true;
    }
    function step(delta) {
        const list = siblings();
        const i = list.indexOf(openRow) + delta;
        if (i >= 0 && i < list.length) open(list[i], { keepFocus: true });
    }
    prevBtn.addEventListener('click', () => step(-1));
    nextBtn.addEventListener('click', () => step(1));
    closeBtn.addEventListener('click', close);
    modal.querySelector('.att-appr-backdrop').addEventListener('click', close);

    // 행·「상세」 버튼은 문서에 위임한다 — 제자리 교체 뒤에도 그대로 동작하도록.
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-detail]');
        if (!trigger || !document.getElementById(trigger.dataset.detail)) return;
        // 표 행 안의 취소 폼·링크는 제 일을 한다.
        if (trigger.tagName === 'TR' && e.target.closest('form, a, button')) return;
        e.preventDefault();
        const row = trigger.classList.contains('att-row') ? trigger : (trigger.closest('.att-row') || trigger);
        open(row);
    });
    document.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target instanceof Element && e.target.matches('tr.att-row[data-detail]')) {
            e.preventDefault();
            open(e.target);
        }
    });

    // 업무함·알림의 딥링크 #apprDetail-<id>
    const openLinked = () => {
        const id = location.hash.slice(1);
        if (!id) return;
        const row = Array.from(document.querySelectorAll('.att-row[data-detail]')).find((r) => r.dataset.detail === id);
        if (row) open(row);
    };
    openLinked();
    window.addEventListener('hashchange', openLinked);

    document.addEventListener('keydown', (e) => {
        if (modal.style.display === 'none') return;
        if (e.key === 'Escape') {
            if (document.querySelector('.confirm-dialog-overlay')) return; // 확인 대화상자가 먼저 닫힌다
            close();
        } else if (e.altKey && e.key === 'ArrowLeft') {
            step(-1);
        } else if (e.altKey && e.key === 'ArrowRight') {
            step(1);
        }
    });

    // ── 승인/반려 (상세 블록이 드나드므로 모달 본문에 위임) ──
    function lock(form) {
        form.querySelectorAll('button').forEach((b) => { b.disabled = true; });
        form.setAttribute('aria-busy', 'true');
    }
    function unlock(form) {
        form.querySelectorAll('button').forEach((b) => { b.disabled = false; });
        form.removeAttribute('aria-busy');
        delete form.dataset.busy;
    }
    body.addEventListener('input', (e) => {
        if (e.target instanceof Element && e.target.matches('textarea[name="note"]')) e.target.removeAttribute('aria-invalid');
    });
    body.addEventListener('click', (e) => {
        const btn = e.target.closest('.att-appr-approve, .att-appr-reject');
        if (!btn) return;
        const form = btn.closest('form.att-appr-decide');
        if (!form || form.dataset.busy) return;
        const approve = btn.classList.contains('att-appr-approve');
        const note = form.querySelector('textarea[name="note"]');
        if (!approve && (!note || !note.value.trim())) {
            if (note) {
                note.setAttribute('aria-invalid', 'true');
                note.focus();
            }
            toast(form.dataset.noteRequired || '반려 사유를 입력해주세요', 'error');
            return;
        }
        const action = approve ? form.dataset.approve : form.dataset.reject;
        if (form.dataset.inline === '1') {
            decideInline(form, action);
            return;
        }
        form.dataset.busy = '1';
        form.action = action;
        lock(form);
        form.submit();
    });

    async function decideInline(form, action) {
        form.dataset.busy = '1';
        lock(form);
        const currentID = openDetail ? openDetail.dataset.request : '';
        const group = openRow ? openRow.dataset.group : 'pending';
        const pendingBefore = decidableRows(group).map((r) => r.dataset.detail);
        let res;
        let payload = {};
        try {
            res = await fetch(action, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-Work-Inline': '1', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
                body: new URLSearchParams(new FormData(form)),
            });
            try { payload = await res.json(); } catch (err) { payload = {}; }
        } catch (err) {
            toast(modal.dataset.failed, 'error');
            unlock(form);
            return;
        }
        if (!res.ok) {
            toast(payload.message || modal.dataset.failed, 'error');
            unlock(form);
            if (res.status === 409 || res.status === 404) await refreshLists(); // 이미 처리된 문서 — 목록만 새로
            return;
        }
        toast(payload.message || '', 'success');
        await refreshLists();
        // 방금 처리한 문서 다음의 결정할 수 있는 문서, 없으면 남은 첫 문서(대행 화면에서는
        // 같은 문서가 다음 단계로 남아 있을 수 있다), 그것도 없으면 닫는다.
        const remaining = decidableRows(group);
        let next = null;
        if (remaining.length) {
            const idx = pendingBefore.indexOf('apprDetail-' + currentID);
            const after = pendingBefore.slice(idx + 1).map((id) => remaining.find((r) => r.dataset.detail === id)).find(Boolean);
            next = after || remaining[0];
        }
        if (next) open(next, { keepFocus: true });
        else {
            close();
            toast(modal.dataset.allDone, 'info');
        }
    }

    // 같은 URL 을 다시 받아 목록 루트([data-att-root] — 결재함 #attApprovals 또는 관리자
    // 현황 #attAdminRequests, 스태시 포함)를 제자리 교체한다 — 새로고침도, 스크롤 튐도 없다.
    async function refreshLists() {
        const root = document.querySelector('[data-att-root]');
        if (!root) return;
        let res;
        try {
            res = await fetch(location.pathname + location.search, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } });
        } catch (err) { return; }
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.getElementById(root.id);
        if (!fresh) return;
        returnDetail(); // 열린 상세를 옛 스태시에 돌려준 뒤 통째로 갈아끼운다
        root.replaceWith(fresh);
        applyAttendanceFilters();
        syncBulkBar();
        document.dispatchEvent(new CustomEvent('attendance:approvals-refreshed'));
    }

    // ── 일괄 승인 (AO4): 대기 행의 체크박스 → 선택 막대 → 순차 승인 → 제자리 교체 ──
    const picked = () => Array.from(document.querySelectorAll('.att-pick:checked'));
    function syncBulkBar() {
        const bar = document.getElementById('attBulkBar');
        if (!bar) return;
        const n = picked().length;
        bar.hidden = n === 0;
        const count = bar.querySelector('[data-bulk-count]');
        if (count) count.textContent = fmtCount(bar.dataset.countTpl || '%d', n);
        const all = document.querySelector('[data-pick-all]');
        if (all) {
            const total = document.querySelectorAll('.att-pick').length;
            all.checked = total > 0 && n === total;
            all.indeterminate = n > 0 && n < total;
        }
    }
    document.addEventListener('change', (e) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        if (e.target.matches('[data-pick-all]')) {
            document.querySelectorAll('.att-pick').forEach((box) => { box.checked = e.target.checked; });
            syncBulkBar();
        } else if (e.target.matches('.att-pick')) {
            syncBulkBar();
        }
    });
    document.addEventListener('click', async (e) => {
        const clear = e.target.closest('[data-bulk-clear]');
        if (clear) {
            document.querySelectorAll('.att-pick').forEach((box) => { box.checked = false; });
            syncBulkBar();
            return;
        }
        const go = e.target.closest('[data-bulk-approve]');
        if (!go) return;
        const bar = document.getElementById('attBulkBar');
        const ids = picked().map((box) => box.dataset.pick);
        if (!bar || !ids.length || bar.dataset.busy) return;
        const ok = window.appConfirm
            ? await window.appConfirm({ message: fmtCount(bar.dataset.confirmTpl, ids.length), okLabel: go.textContent.trim() })
            : window.confirm(fmtCount(bar.dataset.confirmTpl, ids.length));
        if (!ok) return;
        bar.dataset.busy = '1';
        bar.querySelectorAll('button').forEach((b) => { b.disabled = true; });
        const noteEl = bar.querySelector('.att-bulk-note');
        const note = noteEl ? noteEl.value.trim() : '';
        let done = 0;
        let failed = 0;
        for (const id of ids) {
            const item = document.querySelector('.att-pick[data-pick="' + id + '"]');
            const wrap = item ? item.closest('.att-appr-item') : null;
            if (wrap) wrap.dataset.busy = '1';
            try {
                const res = await fetch('/attendance/approvals/' + encodeURIComponent(id) + '/approve', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'X-Work-Inline': '1', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
                    body: new URLSearchParams({ note }),
                });
                if (res.ok) done += 1; else failed += 1;
            } catch (err) {
                failed += 1;
            }
        }
        if (done) toast(fmtCount(bar.dataset.doneTpl, done), 'success');
        if (failed) toast(fmtCount(bar.dataset.failedTpl, failed), 'error');
        await refreshLists();
    });
    syncBulkBar();
}
